import {useHeaderHeight} from '@react-navigation/elements';
import type {StackScreenProps} from '@react-navigation/stack';
import {observer} from 'mobx-react-lite';
import {useCallback, useMemo} from 'react';

import {
  useNavigationGetIsFocused,
  useStackNavigationState,
} from '../../Navigation';
import {useRoot} from '../../Root';
import type {RootStackParamList} from '../../RootStack/RootStackParamList';
import ShowProgressScreen from '../../screens/ShowProgressScreen';
import type {Millisecond} from '../../Time';
import Announcement from './Announcement';
import ShowProgressBindingStateImpl from './ShowProgressBindingStateImpl';
import useQuoteOfTheDay from './useQuoteOfTheDay';
import useTabKey from './useTabKey';

export type ShowProgressBindingProps = StackScreenProps<
  RootStackParamList,
  'ShowProgress'
>;

export default function ShowProgressBinding(props: ShowProgressBindingProps) {
  const {navigation, route} = props;
  return (
    <ShowProgressStableBinding navigation={navigation} routeKey={route.key} />
  );
}

type ShowProgressStableBindingProps = Pick<
  ShowProgressBindingProps,
  'navigation'
> & {
  routeKey: string;
};

const ShowProgressStableBinding = observer(function ShowProgressStableBinding(
  props: ShowProgressStableBindingProps,
) {
  const {navigation, routeKey} = props;
  const provider = useStackNavigationState(navigation);
  const getIsFocused = useNavigationGetIsFocused(routeKey, provider);

  const [getTabKey, setTabKey] = useTabKey(navigation, routeKey, provider);

  const showDaily = useCallback(() => {
    navigation.navigate('ShowDaily');
  }, [navigation]);

  const promptSetup = useCallback(() => {
    navigation.navigate('PromptSetup');
  }, [navigation]);

  const listCities = useCallback(() => {
    navigation.navigate('ListCities');
  }, [navigation]);

  const headerHeight = useHeaderHeight();

  const root = useRoot();
  const state = useMemo(() => new ShowProgressBindingStateImpl(root), [root]);

  const announcement = useMemo(
    () => <Announcement promptSetup={promptSetup} />,
    [promptSetup],
  );

  const now = root.currentTime.wrapped.startOf('day').valueOf() as Millisecond;
  const {body} = useQuoteOfTheDay(now, {
    period: root.currentTime.period,
    skip: !getIsFocused(),
  });

  return (
    <ShowProgressScreen
      today={state.today}
      onTodayPress={showDaily}
      announcement={announcement}
      getTabKey={getTabKey}
      setTabKey={setTabKey}
      getIsFocused={getIsFocused}
      dailyAchievement={state.dailyAchievement}
      anniversaryAchievement={state.anniversaryAchievement}
      congratulation="Сегодня юбилей!"
      quote={body}
      onQuotePress={showDaily}
      onGroupsPress={listCities}
      compensateHeaderHeight={headerHeight}
    />
  );
});
